import { signUpConfirmation, resendConfirmationCode } from "@goodfill/backend-api-client";
import { useState } from "react";
import { FormValidationResult } from "../../../form/formValidation";
import { Modal, Paper, Flex, Heading3, Spinner, Span } from "../../../ui-components";
import { toastErrorHandler } from "../../../unexpectedErrorHandler";
import { backendFetcher } from "../../backendApi/backendApi";
import { ConfirmationCodeForm } from "./ConfirmationCodeForm";
import { useLogout } from "../../useLogout";

interface Props {
  isActive: boolean;
  onSuccess: () => void;
  loginData: { username: string; password: string };
  isLoading: boolean;
}

export const ConfirmationModal: React.FC<Props> = ({ isActive, onSuccess, loginData, isLoading }) => {
  const [isInternalLoading, setIsInternalLoading] = useState(false);

  const [backendCodeErrors, setBackendCodeErrors] = useState([] as FormValidationResult);

  const logout = useLogout();

  const handleSubmitConfirmation = async (code: string) => {
    setIsInternalLoading(true);
    (await signUpConfirmation(backendFetcher, { code: code, username: loginData.username })).match({
      success: () => {},
      businessMatcher: {
        expiredCode: (e) => setBackendCodeErrors([e.message]),
        codeMismatch: (e) => setBackendCodeErrors([e.message]),
        notAuthorized: (e) => {
          if (e.message === "User cannot be confirmed. Current status is CONFIRMED") {
            // user is already confirmed, this is same as success
          } else {
            toastErrorHandler(e);
          }
        },
        userNotFound: toastErrorHandler,
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });
    setIsInternalLoading(false);
    onSuccess();
  };

  const handleResend = async () => {
    setIsInternalLoading(true);
    (await resendConfirmationCode(backendFetcher, { username: loginData.username })).match({
      success: () => {},
      businessMatcher: {
        limitExceeded: (e) => setBackendCodeErrors([e.message]),
        userNotFound: toastErrorHandler,
        userAlreadyConfirmed: () => {
          // If user is already confirmed, we just do onSuccess.
          // This should lead to user being signed-in.
          onSuccess();
        },
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });
    setIsInternalLoading(false);
  };

  const isTotalLoading = isInternalLoading || isLoading;

  return (
    <Modal isActive={isActive} onDismiss={() => {}} setIsActive={() => {}}>
      <Paper white>
        <div className="py-12 px-12 flex flex-col items-center">
          <Flex direction="row" justify="between" gap={2}>
            <Heading3>Enter the OTP</Heading3>
            {isTotalLoading ? <Spinner size={"2em"} style={{ marginTop: "4px" }} /> : null}
          </Flex>
          <Flex>OTP sent to your Email</Flex>
          <ConfirmationCodeForm
            errors={backendCodeErrors}
            onSubmit={handleSubmitConfirmation}
            isLoading={isTotalLoading}
          />

          <Flex direction="rowCol" gap={0} justify={"center"} verticalMargin={3}>
            <Span size="sm" selectNone={true}>
              Didn't get OTP?
            </Span>
            <Span
              data-testid="resendConfirmationLink"
              id="resendConfirmationLink"
              primary={true}
              size="sm"
              selectNone={true}
              onClick={handleResend}
            >
              <a>Resend</a>
            </Span>
          </Flex>
        </div>
      </Paper>
    </Modal>
  );
};
